import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  UseDrawerParams,
  UseToastParams,
  useDrawer,
  useToast,
} from '@electricjs/arc';

const NAV_PREF_KEY = 'nav-open-pref';

const getSavedNavPref = (): boolean => {
  try {
    const saved = localStorage.getItem(NAV_PREF_KEY);
    return saved === null ? true : saved === 'true';
  } catch {
    return true;
  }
};

const saveNavPref = (open: boolean) => {
  try {
    localStorage.setItem(NAV_PREF_KEY, String(open));
  } catch {
    // localStorage unavailable — silent fail
  }
};

type GlobalUIContextType = {
  /* Modal */
  openModal: (component: ReactNode) => void;
  closeModal: () => void;
  isModalOpen: boolean;
  modal: ReactNode;
  /* Drawer */
  openGlobalDrawer: (
    component: ReactNode,
    drawerProps?: UseDrawerParams
  ) => void;
  closeGlobalDrawer: () => void;
  drawerContent: ReactNode;
  drawerProps: UseDrawerParams;
  /* Toast */
  showToast: (params: UseToastParams) => void;
  showSuccessToast: (overrideParams: UseToastParams) => void;
  showErrorToast: (overrideParams: UseToastParams) => void;
  toast: ReactNode;
  /** Current height of any active top banners (e.g. Product Support/partner-redirect), so the
   * drawer can render below them instead of covering them. Pages that render banners (currently
   * only DefaultPageWrapper) publish their height here instead of GlobalDialogs computing it
   * itself, since that height depends on page-local state (e.g. a redirect banner read from
   * sessionStorage) that isn't available from the outlet's position in the tree. */
  bannerHeight: string;
  setBannerHeight: (value: string) => void;
  /* Side Nav */
  openSideNav: () => void;
  closeSideNav: () => void;
  /** Toggles nav and persists the new state as the user's preference */
  manualToggleSideNav: () => void;
  /** Reads the saved preference from localStorage and applies it */
  restoreSavedNavPreference: () => void;
  showSideNav: () => void;
  hideSideNav: () => void;
  isSideNavOpen?: boolean;
  isSideNavVisible?: boolean;
};

const GlobalUIContext = createContext<GlobalUIContextType>({
  openModal: () => {},
  closeModal: () => {},
  isModalOpen: false,
  modal: null,
  openGlobalDrawer: () => {},
  closeGlobalDrawer: () => {},
  drawerContent: null,
  drawerProps: {},
  showToast: () => {},
  showSuccessToast: () => {},
  showErrorToast: () => {},
  toast: null,
  bannerHeight: '0rem',
  setBannerHeight: () => {},
  openSideNav: () => {},
  closeSideNav: () => {},
  manualToggleSideNav: () => {},
  restoreSavedNavPreference: () => {},
  showSideNav: () => {},
  hideSideNav: () => {},
  isSideNavOpen: true,
  isSideNavVisible: true,
});

type GlobalUIProviderProps = {
  children: ReactNode;
};

/**
 * Handles global state of the UI (e.g., state of toast, modal, drawer, sidebar etc).
 *
 * The global components themselves are rendered separately in specific places of the app.
 *
 * There are some dialog components (toast, modal, drawer) that need to be descendants
 * of all providers (e.g., StorefrontProvider, RouterProvider) so they can interact with
 * their contexts. And they should be rendered/instantiated only once in the tree. But as we have
 * 2 subtrees of routes (with separately "RouterProvider"), we need to repeat them in each subtree.
 * So they were grouped inside "GlobalDialogs" component to be easily repeated in each subtree.
 */
export const GlobalUIProvider = ({ children }: GlobalUIProviderProps) => {
  const { t } = useTranslation();

  const [modal, setModal] = useState<ReactNode>(null);

  const [drawerContent, setDrawerContent] = useState<ReactNode>(null);
  const [drawerOptions, setDrawerOptions] = useState<UseDrawerParams>({});
  const { showDrawer, hideDrawer, drawerProps } = useDrawer(drawerOptions);

  const { toast, showToast } = useToast({ id: 'global-ui-toast-default' });

  const [bannerHeight, setBannerHeight] = useState('0rem');

  const [isSideNavOpen, setIsSideNavOpen] = useState(getSavedNavPref);
  const [isSideNavVisible, setIsSideNavVisible] = useState(true);

  // When the "back" button is pressed, close any open modals
  useEffect(() => {
    const handlePopstate = () => {
      if (modal) setModal(null);
    };

    window.addEventListener('popstate', handlePopstate);

    return () => window.removeEventListener('popstate', handlePopstate);
  }, [modal]);

  return (
    <GlobalUIContext.Provider
      value={{
        openModal: useCallback(component => setModal(component), []),
        closeModal: useCallback(() => setModal(null), []),
        isModalOpen: !!modal,
        modal,
        openGlobalDrawer: useCallback(
          (component, customDrawerProps) => {
            setDrawerContent(component);
            setDrawerOptions({
              testId: 'global-drawer',
              placement: 'right',
              width: '40rem',
              ...customDrawerProps,
            });
            showDrawer();
          },
          [showDrawer]
        ),
        closeGlobalDrawer: useCallback(() => hideDrawer(), [hideDrawer]),
        drawerContent,
        drawerProps,
        showToast: useCallback(params => showToast(params), [showToast]),
        showSuccessToast: useCallback(
          overrideParams =>
            showToast({
              intent: 'success',
              title: t('common:success'),
              ...overrideParams,
            }),
          [showToast, t]
        ),
        showErrorToast: useCallback(
          overrideParams =>
            showToast({
              intent: 'error',
              title: t('common:error'),
              ...overrideParams,
            }),
          [showToast, t]
        ),
        toast,
        bannerHeight,
        setBannerHeight: useCallback(value => setBannerHeight(value), []),
        openSideNav: useCallback(() => setIsSideNavOpen(true), []),
        closeSideNav: useCallback(() => setIsSideNavOpen(false), []),
        manualToggleSideNav: useCallback(() => {
          setIsSideNavOpen(prev => {
            const next = !prev;
            saveNavPref(next);
            return next;
          });
        }, []),
        restoreSavedNavPreference: useCallback(
          () => setIsSideNavOpen(getSavedNavPref()),
          []
        ),
        showSideNav: useCallback(() => setIsSideNavVisible(true), []),
        hideSideNav: useCallback(() => setIsSideNavVisible(false), []),
        isSideNavOpen,
        isSideNavVisible,
      }}>
      {children}
    </GlobalUIContext.Provider>
  );
};

export const useGlobalUI = () => {
  const globalUIValue = useContext(GlobalUIContext);

  return {
    ...globalUIValue,
  };
};
